import { LockClosedIcon } from '@heroicons/react/24/solid';
import { useMemo } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/UI/table';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

import { SPORT_IDS, resolveSportIds } from './consts';

interface PendingTeaserPick {
  id: number;
  sportId: number;
  gameTime: string;
  rating: string;
  playType: string;
}

interface PendingTeaserProduct {
  sportId: number;
  productId: number;
  title: string;
  href: string;
}

interface PendingTeaser {
  count: number;
  picks: PendingTeaserPick[];
  products: PendingTeaserProduct[];
}

const getPending: RequestSchema<PendingTeaser> = {
  input: '/api/v1/release/pending',
};

// sportId -> short label (e.g. 3 -> "NFL") for the (non-secret) sport cell.
const SPORT_SHORT: Record<number, string> = Object.fromEntries(
  Object.entries(SPORT_IDS).map(([key, id]) => [id, key.toUpperCase()]),
);

// Play-shaped, generic placeholders (varied lengths, like real plays) so a
// blurred row reads like an actual pick rather than a solid bar. These are NOT
// real selections — the server never sends the secret, so the text here is a
// stand-in and the blur is purely cosmetic.
const PLACEHOLDERS = [
  '241 Riverside State -6.5 vs. Lakeside College',
  '155 Summit University +3 (-110) at Harbor Bay',
  '410 Granite City / Bayshore Tech Over 48.5',
  '128 Meadowbrook -9.5 vs. Fairview Union',
  '332 Ironwood PK (-120) at Clearwater State',
  '207 Northgate +2.5 (-105) vs. Southridge',
];

/** One paywall section for a single sport's pending picks. */
function SportPendingTeaser({
  product,
  picks,
}: {
  product: PendingTeaserProduct;
  picks: PendingTeaserPick[];
}) {
  const sportLabel = SPORT_SHORT[product.sportId] || '';

  return (
    <section className="overflow-hidden rounded-xl border border-emerald-200 dark:border-emerald-900/60">
      <header className="flex flex-wrap items-center justify-between gap-3 bg-emerald-50/70 px-4 py-2.5 dark:bg-emerald-950/30">
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900 dark:text-emerald-200">
          <LockClosedIcon className="h-4 w-4 shrink-0" />
          {picks.length} pending {sportLabel} {picks.length === 1 ? 'pick' : 'picks'} — subscribe to
          view
        </div>
        <a
          href={product.href}
          className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
        >
          Subscribe to view
        </a>
      </header>

      {/* Mobile (below md): blurred play-cards, matching the graded results cards
          (ResultsCardList) so the locked picks read as the same design — full-width
          (blurred) title + lock, then a "● Pending · SPORT" meta line. */}
      <ul className="flex flex-col gap-2 p-3 md:hidden">
        {picks.map((pick, i) => (
          <li
            key={pick.id}
            className="rounded-xl border border-zinc-950/10 bg-white p-3 shadow-xs dark:border-white/10 dark:bg-zinc-900"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1 font-medium text-sm text-zinc-900 dark:text-white">
                <span
                  aria-hidden
                  className="pointer-events-none select-none break-words text-zinc-700 blur-[4.5px] dark:text-zinc-300"
                >
                  {PLACEHOLDERS[i % PLACEHOLDERS.length]}
                </span>
              </div>
              <LockClosedIcon className="h-4 w-4 shrink-0 text-emerald-500/70 dark:text-emerald-400/60" />
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-zinc-500 dark:text-zinc-400">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 shrink-0 rounded-full bg-amber-300" />
                <span>Pending</span>
              </span>
              {SPORT_SHORT[pick.sportId] && (
                <>
                  <span aria-hidden>·</span>
                  <span>{SPORT_SHORT[pick.sportId]}</span>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Desktop (md+): the table layout, matching the graded results table. */}
      <div className="hidden md:block">
        <Table className="table-auto">
          {/* Invisible header — collapsed to zero height with transparent text —
              reproduces the real results table's column widths (Play is `w-full`)
              so the blurred play text lines up with the graded plays below. */}
          <TableHeader
            aria-hidden
            className="[&_th]:h-0 [&_th]:overflow-hidden [&_th]:border-0 [&_th]:bg-transparent [&_th]:p-0 [&_th]:leading-none [&_th]:text-transparent"
          >
            <TableRow>
              <TableHead breakpoint="md">Sport</TableHead>
              <TableHead className="w-full">Play</TableHead>
              <TableHead>Result</TableHead>
              <TableHead breakpoint="lg">Score</TableHead>
              <TableHead breakpoint="lg">Closing line</TableHead>
              <TableHead>CLV</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {picks.map((pick, i) => (
              <TableRow key={pick.id}>
                <TableCell className="whitespace-nowrap text-xs text-zinc-500" breakpoint="md">
                  {SPORT_SHORT[pick.sportId] || ''}
                </TableCell>
                <TableCell className="w-full">
                  <div className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className="pointer-events-none select-none text-zinc-700 blur-[4.5px] dark:text-zinc-300"
                    >
                      {PLACEHOLDERS[i % PLACEHOLDERS.length]}
                    </span>
                    <LockClosedIcon className="h-3.5 w-3.5 shrink-0 text-emerald-500/70 dark:text-emerald-400/60" />
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <div className="inline-flex items-center space-x-2">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-amber-300" />
                    <span>Pending</span>
                  </div>
                </TableCell>
                <TableCell breakpoint="lg" />
                <TableCell breakpoint="lg" />
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="bg-emerald-50/40 px-4 py-2 text-center text-xs text-gray-500 dark:bg-emerald-950/20 dark:text-gray-400">
        Included with {product.title}. Subscribe to unlock every pick.
      </div>
    </section>
  );
}

/**
 * Public archive paywall teaser. Renders the member-visible PENDING (ungraded),
 * non-free picks for the current sport view as blurred, locked rows that match
 * the real results table — one section PER SPORT, each with its own count and a
 * "Subscribe to view" CTA to that sport's order page.
 *
 * The blur is purely cosmetic — the server never sends the pick selection, so
 * the placeholder text is a generic stand-in, never the real bet. Renders
 * nothing when there are no qualifying pending picks.
 */
export function PendingPicksTeaser({ sportIdBase }: { sportIdBase?: string }) {
  const sportId = useMemo(() => resolveSportIds(sportIdBase), [sportIdBase]);

  const request = useRequest(
    getPending,
    useMemo(
      () => ({
        query: { data: sportId != null ? { sportId } : {} },
        cache: { maxAge: 60 * 1000 },
      }),
      [sportId],
    ),
  );

  const data = request.data;

  const sections = useMemo(() => {
    if (!data?.count) {
      return [];
    }

    const picksBySport = new Map<number, PendingTeaserPick[]>();
    for (const pick of data.picks) {
      const list = picksBySport.get(pick.sportId);
      if (list) {
        list.push(pick);
      } else {
        picksBySport.set(pick.sportId, [pick]);
      }
    }

    // One section per sport that has a CTA product, ordered by the archive's
    // sport order when it's a multi-sport view, otherwise by the product list.
    const order = Array.isArray(sportId) ? sportId : [];
    return data.products
      .map((product) => ({ product, picks: picksBySport.get(product.sportId) || [] }))
      .filter((s) => s.picks.length > 0)
      .sort((a, b) => order.indexOf(a.product.sportId) - order.indexOf(b.product.sportId));
  }, [data, sportId]);

  if (!sections.length) {
    return null;
  }

  return (
    <div className="mt-8 space-y-6">
      {sections.map(({ product, picks }) => (
        <SportPendingTeaser key={product.sportId} product={product} picks={picks} />
      ))}
    </div>
  );
}
